import type { ReactNode } from "react";
import { motion } from "framer-motion";
import { Sparkles, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

/** The card surface both insight cards sit on — the brand `.surface-card`,
 * extracted so the two can never drift apart. */
export function InsightShell({ children, delay = 0.1 }: { children: ReactNode; delay?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="surface-card p-5 sm:p-6"
    >
      {children}
    </motion.div>
  );
}

/**
 * Shared header row: sparkle, title, optional period label, then a right-hand
 * cluster for the "AI insight" badge and (Monthly) the period picker.
 *
 * The badge is only ever rendered when a model actually produced the prose —
 * never for locally-derived copy.
 */
export function InsightHeader({
  title,
  meta,
  showAiBadge,
  collapsed,
  onToggle,
  bodyId,
  right,
}: {
  title: string;
  meta?: string;
  showAiBadge?: boolean;
  /** Extra control rendered before the collapse chevron (e.g. the granularity selector). */
  right?: ReactNode;
  /** Omit `onToggle` to render a non-collapsible header. */
  collapsed?: boolean;
  onToggle?: () => void;
  /** id of the region the chevron controls, for `aria-controls`. */
  bodyId?: string;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <Sparkles className="h-4 w-4 shrink-0 text-primary" />
      <span className="text-sm font-semibold text-foreground">{title}</span>
      {meta && (
        <>
          <span className="text-sm text-muted-foreground">·</span>
          <span className="truncate text-sm text-muted-foreground">{meta}</span>
        </>
      )}
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {showAiBadge && (
          <span className="hidden items-center gap-1.5 rounded-full bg-primary/[0.07] px-2.5 py-1 text-[11px] font-medium text-primary sm:inline-flex">
            <Sparkles className="h-3 w-3" />
            AI insight
          </span>
        )}
        {right}
        {onToggle && (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={!collapsed}
            aria-controls={bodyId}
            aria-label={collapsed ? `Show ${title}` : `Hide ${title}`}
            className="flex h-7 w-7 items-center justify-center rounded-full border border-foreground/10 text-foreground/45 transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
          >
            <ChevronDown
              className={cn("h-4 w-4 transition-transform duration-200", !collapsed && "rotate-180")}
            />
          </button>
        )}
      </div>
    </div>
  );
}
